// R-239: a segmented one-time-code field — one box per digit.
//
// It replaces a single `maxLength={6}` text input at three call sites (login MFA,
// the step-up dialog, and MFA recovery). The value is still ONE string: the boxes
// are presentation, and the caller keeps the same `value`/`onChange` contract it
// had with the old input.
//
// ⚠ Why real inputs rather than one field behind six painted boxes: a single
// hidden input cannot be corrected in the middle. An operator who mistypes digit
// three expects to click digit three, and with painted boxes there is nothing to
// click. Six inputs also give the browser somewhere to put each character of an
// autofilled SMS code.
import { useEffect, useMemo, useRef } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { styles } from './otp-input.styles';
interface OtpInputProps {
  /** The code so far — always digits only, never longer than `length`. */
  value: string;
  onChange: (next: string) => void;
  /** Fired when the last box is filled, so a form can submit without a click. */
  onComplete?: (code: string) => void;
  length?: number;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  /** Labels the GROUP; each box is labelled "digit N of M" for a screen reader. */
  ariaLabel?: string;
  /** `digit N of M`, with `{n}` and `{total}` — passed in so this stays i18n-free. */
  digitLabel?: (n: number, total: number) => string;
}
export function OtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled = false,
  invalid = false,
  autoFocus = false,
  ariaLabel,
  digitLabel
}: OtpInputProps) {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  const digits = useMemo(() => Array.from({
    length
  }, (_, i) => value[i] ?? ''), [value, length]);
  useEffect(() => {
    if (autoFocus) boxes.current[0]?.focus();
  }, [autoFocus]);

  // Announce completion once the code is whole. Deliberately in an effect rather
  // than inside the keystroke handler: a PASTE fills every box at once, and a
  // submit that only fired on the final keypress would ignore it.
  useEffect(() => {
    if (value.length === length) onComplete?.(value);
  }, [value, length, onComplete]);

  /** Replace the code from `at`, keeping it digits-only and within `length`. */
  const write = (at: number, incoming: string) => {
    const clean = incoming.replace(/\D/g, '');
    if (clean === '') return;
    const next = (value.slice(0, at) + clean).slice(0, length);
    onChange(next);
    // Land on the first still-empty box, or the last one when the code is full —
    // so a paste leaves the caret where a correction would go.
    const target = Math.min(next.length, length - 1);
    boxes.current[target]?.focus();
    boxes.current[target]?.select();
  };
  const onKeyDown = (i: number, e: KeyboardEvent) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[i] !== '') {
        // Clear this box and stay — deleting a digit you can see should not also
        // move you somewhere else.
        onChange(value.slice(0, i) + value.slice(i + 1));
        return;
      }
      // Empty box: step back and clear that one, which is what a single
      // backspace-to-fix feels like.
      if (i > 0) {
        onChange(value.slice(0, i - 1) + value.slice(i));
        boxes.current[i - 1]?.focus();
      }
      return;
    }
    if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      boxes.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && i < length - 1) {
      e.preventDefault();
      boxes.current[i + 1]?.focus();
    }
  };
  return <div {...stylex.props(styles.row)} role="group" aria-label={ariaLabel}>
      {digits.map((d, i) => <input
    // eslint-disable-next-line react/no-array-index-key -- position IS the identity
    key={i} ref={el => {
      boxes.current[i] = el;
    }} type="text" inputMode="numeric"
    // One-time-code autofill belongs on the FIRST box only; on every box the
    // browser offers to fill each one with the whole code.
    {...i === 0 ? {
      autoComplete: 'one-time-code' as const
    } : {
      autoComplete: 'off' as const
    }}
    // 2, not 1: a browser autofilling a 6-digit code writes it into the
    // focused box, and maxLength 1 would silently truncate it to the first
    // digit. `write` re-distributes whatever arrives.
    maxLength={2} value={d} disabled={disabled} aria-label={digitLabel?.(i + 1, length)} aria-invalid={invalid || undefined} {...stylex.props(styles.box, d !== '' && styles.boxFilled, invalid && styles.boxInvalid)} onFocus={e => (e.currentTarget as HTMLInputElement).select()} onInput={e => write(i, (e.currentTarget as HTMLInputElement).value)} onPaste={e => {
      e.preventDefault();
      write(0, e.clipboardData?.getData('text') ?? '');
    }} onKeyDown={e => onKeyDown(i, e as unknown as KeyboardEvent)} />)}
    </div>;
}